import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calculator,
  FileText,
  DollarSign,
  Building,
  ArrowRight,
  Phone,
  Mail,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layouts/MainLayout";

const TaxInformation = () => {
  // Current date for display
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  return (
    <>
      <Helmet>
        <title>2025 Tax Information | Numberz Insight</title>
        <meta
          name="description"
          content="Current Australian tax rates, ASIC fees, and ABN registration information for 2025. Professional tax services in Melbourne and Launceston."
        />
        <meta
          name="keywords"
          content="tax rates 2025, ASIC fees, ABN registration, Australian taxation, tax agent Melbourne, tax agent Launceston"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-b from-primary/10 to-background dark:from-primary/5 dark:to-background py-16 md:py-24">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-left"
                >
                  <Badge
                    variant="outline"
                    className="mb-4 px-3 py-1 border-primary/30 text-primary"
                  >
                    <Calendar className="w-3 h-3 mr-1 inline" /> Updated April
                    2025
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    Australian Tax Rates & Fees 2025
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Current tax rates, ASIC company registration fees, and ABN
                    information from official government sources.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button size="lg" asChild>
                      <Link to="/dashboard/tax-return">
                        Start Tax Return <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="#contact">
                        Contact Our Tax Agents{" "}
                        <Phone className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Tax planning and financial documents"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Tax Information Tabs */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <Tabs defaultValue="income-tax" className="w-full">
                <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 mb-8">
                  <TabsTrigger value="income-tax">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Income Tax Rates
                  </TabsTrigger>
                  <TabsTrigger value="company-fees">
                    <Building className="h-4 w-4 mr-2" />
                    ASIC Company Fees
                  </TabsTrigger>
                  <TabsTrigger value="abn-info">
                    <FileText className="h-4 w-4 mr-2" />
                    ABN Information
                  </TabsTrigger>
                </TabsList>

                {/* Income Tax Rates Tab */}
                <TabsContent value="income-tax" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <DollarSign className="h-5 w-5 mr-2 text-primary" />
                        Australian Resident Tax Rates 2024-25
                      </CardTitle>
                      <CardDescription>
                        Current income tax rates for Australian residents. These
                        rates do not include the Medicare levy of 2%.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Taxable Income</TableHead>
                            <TableHead>Tax on this Income</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell>$0 – $18,200</TableCell>
                            <TableCell>Nil</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>$18,201 – $45,000</TableCell>
                            <TableCell>16c for each $1 over $18,200</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>$45,001 – $135,000</TableCell>
                            <TableCell>
                              $4,288 plus 30c for each $1 over $45,000
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>$135,001 – $190,000</TableCell>
                            <TableCell>
                              $31,288 plus 37c for each $1 over $135,000
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>$190,001 and over</TableCell>
                            <TableCell>
                              $51,638 plus 45c for each $1 over $190,000
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                        <h4 className="font-medium mb-2">Important Notes:</h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>
                            The above rates do not include the Medicare levy of
                            2%.
                          </li>
                          <li>
                            Foreign residents are taxed at different rates.
                          </li>
                          <li>
                            Working holiday makers are taxed at special rates.
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <p className="text-sm text-muted-foreground">
                        Source: Australian Taxation Office, updated April 2025
                      </p>
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href="https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on ATO Website
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>

                {/* ASIC Company Fees Tab */}
                <TabsContent value="company-fees" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Building className="h-5 w-5 mr-2 text-primary" />
                        ASIC Company Registration Fees 2024-25
                      </CardTitle>
                      <CardDescription>
                        Current fees for registering a company in Australia.
                        Fees are not subject to GST.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Registration Type</TableHead>
                            <TableHead>Fee</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              Proprietary company with share capital
                            </TableCell>
                            <TableCell>$597</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Proprietary company without share capital
                            </TableCell>
                            <TableCell>$491</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Public company with share capital
                            </TableCell>
                            <TableCell>$597</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              Public company without share capital
                            </TableCell>
                            <TableCell>$491</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Reserve a company name</TableCell>
                            <TableCell>$61</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>

                      <div className="mt-8">
                        <h4 className="text-lg font-medium mb-4">
                          Annual Review Fees
                        </h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Company Type</TableHead>
                              <TableHead>Annual Fee</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell>Proprietary company</TableCell>
                              <TableCell>$321</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Public company</TableCell>
                              <TableCell>$1,492</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Special purpose company (proprietary)
                              </TableCell>
                              <TableCell>$65</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Special purpose company (public)
                              </TableCell>
                              <TableCell>$61</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <p className="text-sm text-muted-foreground">
                        Source: ASIC, updated April 2025
                      </p>
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href="https://asic.gov.au/for-business/payments-fees-and-invoices/asic-fees/fees-for-commonly-lodged-documents/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on ASIC Website
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>

                {/* ABN Information Tab */}
                <TabsContent value="abn-info" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-primary" />
                        ABN Registration Information
                      </CardTitle>
                      <CardDescription>
                        Information about Australian Business Number (ABN)
                        registration and related business registrations.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-medium mb-2">
                            What is an ABN?
                          </h4>
                          <p>
                            An ABN is a unique 11-digit number that identifies
                            your business to the government and community.
                            Having an ABN allows you to:
                          </p>
                          <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>
                              Confirm your business identity to others when
                              ordering and invoicing
                            </li>
                            <li>
                              Avoid having business clients withhold a
                              percentage of payments they make to you
                            </li>
                            <li>Register a business name</li>
                            <li>
                              Register for GST and other business tax
                              registrations
                            </li>
                            <li>
                              Register a '.com.au', '.net.au', or '.org.au'
                              domain name
                            </li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-lg font-medium mb-2">
                            Registration Fees
                          </h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Registration Type</TableHead>
                                <TableHead>Fee</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell>ABN Registration</TableCell>
                                <TableCell>Free</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  Business Name Registration (1 year)
                                </TableCell>
                                <TableCell>$44</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  Business Name Registration (3 years)
                                </TableCell>
                                <TableCell>$102</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>GST Registration</TableCell>
                                <TableCell>Free</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  PAYG Withholding Registration
                                </TableCell>
                                <TableCell>Free</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>

                        <div className="p-4 bg-muted/50 rounded-lg">
                          <h4 className="font-medium mb-2">
                            Important Information:
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>
                              You must register for GST if your business has a
                              GST turnover of $75,000 or more.
                            </li>
                            <li>
                              You must register for GST regardless of turnover
                              if you provide taxi or limousine travel (including
                              ride-sharing).
                            </li>
                            <li>
                              Business name registration doesn't give you
                              exclusive rights to that name. Consider trademark
                              protection.
                            </li>
                            <li>
                              If you're applying for an ABN for a partnership,
                              company, or trust, you'll need a business TFN
                              (automatically issued).
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <p className="text-sm text-muted-foreground">
                        Source: Australian Business Register, updated April 2025
                      </p>
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href="https://register.business.gov.au/registration/type"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Visit Business Registration Service
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </section>

          {/* Why Choose Us Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Why Choose Numberz Insight
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Our team of experienced tax professionals can help you
                  navigate the complexities of Australian taxation.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <Calculator className="h-10 w-10 text-primary" />,
                    title: "Expert Tax Planning",
                    description:
                      "Strategic tax planning to minimize your tax liability and maximize your returns.",
                  },
                  {
                    icon: <FileText className="h-10 w-10 text-primary" />,
                    title: "Business Registration",
                    description:
                      "Assistance with ABN, GST, and company registrations to get your business started right.",
                  },
                  {
                    icon: <DollarSign className="h-10 w-10 text-primary" />,
                    title: "Tax Return Preparation",
                    description:
                      "Accurate and timely preparation of individual and business tax returns.",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-card dark:bg-gray-800 p-6 rounded-xl shadow-md border border-border dark:border-gray-700"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4 p-3 bg-primary/10 rounded-full">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-2">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section
            id="contact"
            className="py-16 bg-primary/5 dark:bg-primary/10"
          >
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Get Started?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    Contact our team of tax professionals today for a
                    consultation. We're here to help with all your tax and
                    business registration needs.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 mr-3 text-primary" />
                      <span>1300 123 435</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 mr-3 text-primary" />
                      <a
                        href="mailto:tax.agents@numberz.au"
                        className="text-primary hover:underline"
                      >
                        tax.agents@numberz.au
                      </a>
                    </div>
                  </div>
                </div>
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Request a Consultation</CardTitle>
                      <CardDescription>
                        Fill out the form below and one of our tax professionals
                        will contact you shortly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg" asChild>
                          <Link to="/dashboard/tax-return">
                            Start Tax Return Process
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default TaxInformation;
